interface BeforeInstallPromptEvent extends Event {
	prompt(): Promise<void>;
	userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "install-prompt-dismissed";

const template = document.createElement("template");
template.innerHTML = `
	<style>
		:host {
			display: block;
			position: fixed;
			bottom: var(--space-lg);
			left: var(--space-md);
			right: var(--space-md);
			z-index: 1000;
		}

		.prompt {
			display: flex;
			align-items: center;
			gap: var(--space-md);
			padding: var(--space-md);
			background: var(--color-surface);
			border-radius: var(--radius-md);
			box-shadow: var(--shadow);
		}

		.prompt[hidden] {
			display: none;
		}

		.content {
			flex: 1;
		}

		.title {
			font-weight: 600;
			margin-bottom: var(--space-xs);
		}

		.description {
			font-size: 0.875rem;
			color: var(--color-text-muted);
		}

		.actions {
			display: flex;
			gap: var(--space-sm);
		}

		button {
			padding: var(--space-sm) var(--space-md);
			border-radius: var(--radius-sm);
			font-size: 0.875rem;
			border: none;
			cursor: pointer;
			transition: background-color 0.2s;
		}

		.install-btn {
			background: var(--color-accent);
			color: var(--color-text);
		}

		.install-btn:hover {
			background: #ff6b81;
		}

		.dismiss-btn {
			background: transparent;
			color: var(--color-text-muted);
		}

		.dismiss-btn:hover {
			background: var(--color-primary);
		}

		.ios-instructions {
			font-size: 0.875rem;
			color: var(--color-text-muted);
		}

		.ios-instructions code {
			background: var(--color-primary);
			padding: 2px 6px;
			border-radius: var(--radius-sm);
			font-family: var(--font-mono);
		}
	</style>
	<div class="prompt" hidden>
		<div class="content">
			<div class="title">Install WAYF</div>
			<div class="description"></div>
		</div>
		<div class="actions">
			<button class="dismiss-btn">Not now</button>
			<button class="install-btn">Install</button>
		</div>
	</div>
`;

class InstallPrompt extends HTMLElement {
	private prompt: HTMLElement;
	private description: HTMLElement;
	private installBtn: HTMLButtonElement;
	private dismissBtn: HTMLButtonElement;
	private deferredPrompt: BeforeInstallPromptEvent | null = null;
	private isIOS = false;

	constructor() {
		super();
		this.attachShadow({ mode: "open" });
		this.shadowRoot!.appendChild(template.content.cloneNode(true));

		this.prompt = this.shadowRoot!.querySelector(".prompt")!;
		this.description = this.shadowRoot!.querySelector(".description")!;
		this.installBtn = this.shadowRoot!.querySelector(".install-btn")!;
		this.dismissBtn = this.shadowRoot!.querySelector(".dismiss-btn")!;
	}

	connectedCallback() {
		if (this.isStandalone() || this.wasDismissed()) {
			return;
		}

		this.isIOS = this.detectIOS();

		if (this.isIOS) {
			this.showIOSPrompt();
		} else {
			window.addEventListener("beforeinstallprompt", this.handleBeforeInstall);
		}

		this.installBtn.addEventListener("click", this.handleInstall);
		this.dismissBtn.addEventListener("click", this.handleDismiss);
	}

	disconnectedCallback() {
		window.removeEventListener("beforeinstallprompt", this.handleBeforeInstall);
		this.installBtn.removeEventListener("click", this.handleInstall);
		this.dismissBtn.removeEventListener("click", this.handleDismiss);
	}

	private isStandalone(): boolean {
		return (
			window.matchMedia("(display-mode: standalone)").matches ||
			(navigator as unknown as { standalone?: boolean }).standalone === true
		);
	}

	private detectIOS(): boolean {
		return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as { MSStream?: unknown }).MSStream;
	}

	private wasDismissed(): boolean {
		const dismissed = localStorage.getItem(DISMISSED_KEY);
		if (!dismissed) {
			return false;
		}
		const dismissedAt = Number.parseInt(dismissed, 10);
		const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
		return daysSince < 7;
	}

	private showIOSPrompt() {
		this.description.innerHTML = "Tap <code>Share</code> then <code>Add to Home Screen</code>";
		this.installBtn.hidden = true;
		this.prompt.hidden = false;
	}

	private handleBeforeInstall = (e: Event) => {
		e.preventDefault();
		this.deferredPrompt = e as BeforeInstallPromptEvent;
		this.description.textContent = "Add to your home screen for quick access";
		this.prompt.hidden = false;
	};

	private handleInstall = async () => {
		if (!this.deferredPrompt) {
			return;
		}
		await this.deferredPrompt.prompt();
		const { outcome } = await this.deferredPrompt.userChoice;
		if (outcome === "accepted") {
			this.prompt.hidden = true;
		}
		this.deferredPrompt = null;
	};

	private handleDismiss = () => {
		localStorage.setItem(DISMISSED_KEY, Date.now().toString());
		this.prompt.hidden = true;
	};
}

customElements.define("install-prompt", InstallPrompt);
